import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Plus, Calendar as CalendarIcon, Clock, Users, Video, MoreVertical, Loader2 } from 'lucide-react';
import { eventsApi } from '../../api';
import { Button } from '../ui/button';
import DashboardLayout from './DashboardLayout';
import CreateEventModal from '../modals/CreateEventModal';

const Events = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('upcoming');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [registering, setRegistering] = useState(null);
  const [registeredIds, setRegisteredIds] = useState(() => new Set());

  const handleRegister = async (event) => {
    if (event.status === 'live' && event.meeting_url) {
      window.open(event.meeting_url, '_blank', 'noopener');
      return;
    }
    setRegistering(event.id);
    try {
      await eventsApi.register(event.id);
      setRegisteredIds((prev) => new Set(prev).add(event.id));
      setEvents((prev) => prev.map((e) => e.id === event.id
        ? { ...e, registrations_count: (e.registrations_count || 0) + 1 }
        : e));
    } catch (err) {
      alert(err.response?.data?.detail || 'Registration failed.');
    } finally {
      setRegistering(null);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await eventsApi.list(0, 50);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleEventCreated = (newEvent) => {
    setEvents(prev => [newEvent, ...prev]);
  };

  const now = new Date();
  const filteredEvents = events.filter(event => {
    const eventDate = new Date(event.start_time);
    const matchesSearch = event.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || 
      (activeTab === 'upcoming' && eventDate >= now) ||
      (activeTab === 'past' && eventDate < now) ||
      (activeTab === 'live' && event.status === 'live');
    return matchesSearch && matchesTab;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'workshop': return 'bg-purple-100 text-purple-700';
      case 'ama': return 'bg-blue-100 text-blue-700';
      case 'call': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Create Modal */}
      <CreateEventModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleEventCreated}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('events.title')}</h1>
          <p className="text-gray-500 mt-1">{t('events.subtitle')}</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t('events.create_btn')}
        </Button>
      </div>

      {/* Tabs and Search */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
          {[['upcoming', 'Upcoming'], ['live', 'Live Now'], ['past', 'Past'], ['all', 'All Events']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === key
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {label}
              {key === 'live' && events.filter(e => e.status === 'live').length > 0 && (
                <span className="ml-2 w-2 h-2 bg-red-500 rounded-full inline-block animate-pulse" />
              )}
            </button>
          ))}
        </div>
        <div className="relative flex-1 sm:max-w-xs sm:ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('common.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
      </div>

      {/* Events list */}
      <div className="space-y-4">
        {filteredEvents.map((event) => (
          <div
            key={event.id}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
          >
            <div className="flex flex-col md:flex-row">
              {/* Image */}
              <div className="md:w-64 h-48 md:h-auto relative flex-shrink-0">
                <img
                  src={event.image || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&q=80'}
                  alt={event.title}
                  className="w-full h-full object-cover"
                />
                {event.status === 'live' && (
                  <div className="absolute top-3 left-3 px-3 py-1 bg-red-500 text-white text-sm font-medium rounded-full flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    Live Now
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full ${getTypeColor(event.event_type)} mb-2`}>
                      {(event.event_type || 'event').charAt(0).toUpperCase() + (event.event_type || 'event').slice(1)}
                    </span>
                    <h3 className="text-xl font-semibold text-gray-900">{event.title}</h3>
                  </div>
                  <button className="p-2 text-gray-400 hover:text-gray-600">
                    <MoreVertical className="w-5 h-5" />
                  </button>
                </div>
                
                <p className="text-gray-500 mb-4 line-clamp-2">{event.description}</p>

                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
                  <div className="flex items-center gap-1.5">
                    <CalendarIcon className="w-4 h-4" />
                    <span>{formatDate(event.start_time)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    <span>{event.duration || '1 hour'}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Users className="w-4 h-4" />
                    <span>{event.registrations_count || 0}/{event.max_attendees || 100} attending</span>
                  </div>
                  {event.is_online && (
                    <div className="flex items-center gap-1.5">
                      <Video className="w-4 h-4" />
                      <span>Online</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <span className="text-sm font-medium text-emerald-600">
                        {(event.host_name || 'H').charAt(0)}
                      </span>
                    </div>
                    <span className="text-sm text-gray-600">Hosted by {event.host_name || 'Host'}</span>
                  </div>
                  <Button
                    size="sm"
                    variant={event.status === 'live' ? 'default' : 'outline'}
                    disabled={registering === event.id || registeredIds.has(event.id)}
                    onClick={() => handleRegister(event)}
                  >
                    {registeredIds.has(event.id)
                      ? 'Registered'
                      : event.status === 'live' ? 'Join Now' : 'Register'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredEvents.length === 0 && !loading && (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No events found</h3>
          <p className="text-gray-500">Try adjusting your search or create a new event</p>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Events;
